// Shared by the app (through webpack) and by generateSitemap.js (plain node, through a
// dynamic import). Keeping one copy is the point: a facet URL the build script emits must
// always match a page the app serves, and the alternative — two copies held together by
// "keep in step" comments — silently submits URLs that render as not-found. Pure data and
// pure functions only: no React, no browser globals, so both loaders can take it.

// Below this a facet page is too thin to be worth indexing, so it is served but marked
// noindex, and left out of the sitemap.
export const MIN_ANTIBODIES_PER_FACET = 3;

// The API uses "n/a" and "None" as placeholders for missing values.
export const PLACEHOLDER_VALUES = ['', 'n/a', 'none'];

// "All" and "Other" show up in species fields meaning pan-species and miscellaneous rather
// than a specific organism, so they get no facet page and are left out of SEO copy.
export const VAGUE_SPECIES = ['all', 'other'];

export const cleanText = (value) =>
  typeof value === 'string'
    ? value
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    : '';

export const isMeaningful = (value) => {
  const cleaned = cleanText(value).toLowerCase();

  return cleaned !== '' && !PLACEHOLDER_VALUES.includes(cleaned);
};

export const isSpecificSpecies = (value) =>
  isMeaningful(value) &&
  !VAGUE_SPECIES.includes(cleanText(value).toLowerCase());

export const namesOf = (entries) =>
  (entries || []).map((entry) => cleanText(entry?.name)).filter(isMeaningful);

// One catalog record has an Abcam product URL pasted into the manufacturer field. Extract
// the domain label when a URL is given so it groups under the vendor instead of minting a
// 180-character facet slug.
const extractVendorFromUrl = (value) => {
  const match = /^https?:\/\/(?:www\.)?([^/?#]+)/i.exec(cleanText(value));
  if (!match) return value;

  return match[1].split('.')[0] || value;
};

export const slugifyFacetValue = (value) =>
  String(extractVendorFromUrl(value) || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const EXCLUDED_FACET_VALUES = [
  ...VAGUE_SPECIES,
  ...PLACEHOLDER_VALUES.filter(Boolean),
];

// Vendor names are typed in by hand, so one vendor arrives under several spellings. Every
// variant maps onto a single slug so each vendor gets one page, and the display name is
// pinned so the merged page does not inherit whichever spelling happened to come first.
const MANUFACTURER_ALIASES = {
  bethyl: 'bethyl-laboratories',
  'biorbyt-ltd': 'biorbyt',
  'bioss-antibodies': 'bioss',
  cst: 'cell-signaling-technology',
  'cell-signaling': 'cell-signaling-technology',
  'cell-signaling-technologies': 'cell-signaling-technology',
  'cell-signalling': 'cell-signaling-technology',
  'end-milipore': 'millipore-sigma',
  fisher: 'thermofisher',
  gtx: 'genetex',
  'invitrogen-thermo-fisher': 'invitrogen',
  'leica-biosystems': 'leica',
  'leica-ncl': 'leica',
  'life-span-bio': 'ls-bio',
  lsbio: 'ls-bio',
  millipore: 'millipore-sigma',
  'novus-biologicals': 'novus',
  'protein-tech': 'proteintech',
  'proteintech-group-inc': 'proteintech',
  ptg: 'proteintech',
  'r-d': 'r-d-systems',
  'rockland-client-own-antibody': 'rockland',
  'santa-cruz-biotech': 'santa-cruz',
  sigma: 'millipore-sigma',
  'sigma-aldrich': 'millipore-sigma',
  'sigma-millipore': 'millipore-sigma',
  'sigma-prestige': 'millipore-sigma',
  sigman: 'millipore-sigma',
  thermo: 'thermofisher',
  'thermo-fisher': 'thermofisher',
  'thermofisher-invitrogen': 'invitrogen',
  vector: 'vector-laboratories',
};

const MANUFACTURER_DISPLAY_NAMES = {
  abcam: 'Abcam',
  'atlas-antibodies': 'Atlas Antibodies',
  'bethyl-laboratories': 'Bethyl Laboratories',
  biorbyt: 'Biorbyt',
  bioss: 'Bioss',
  'cell-signaling-technology': 'Cell Signaling Technology',
  dako: 'Dako',
  genetex: 'GeneTex',
  invitrogen: 'Invitrogen',
  leica: 'Leica',
  'ls-bio': 'LSBio',
  'millipore-sigma': 'MilliporeSigma',
  novus: 'Novus',
  proteintech: 'Proteintech',
  'r-d-systems': 'R&D Systems',
  rockland: 'Rockland',
  'santa-cruz': 'Santa Cruz',
  thermofisher: 'Thermo Fisher',
  'vector-laboratories': 'Vector Laboratories',
};

// Which antibody field each facet lists, and how to read it: "named" fields are arrays of
// { name }, "text" is a plain string. The app adds the presentational half (route, labels,
// heading) in antibodyFacets.js; both halves together form a facet.
export const FACET_DESCRIPTORS = [
  { segment: 'applications', field: 'applications', kind: 'named' },
  {
    segment: 'species',
    field: 'histowiz_validated_species',
    kind: 'named',
    excludedValues: EXCLUDED_FACET_VALUES,
  },
  {
    segment: 'manufacturers',
    field: 'manufacturer',
    kind: 'text',
    aliases: MANUFACTURER_ALIASES,
    displayNames: MANUFACTURER_DISPLAY_NAMES,
  },
];

export const valuesOf = (antibody, facet) => {
  const raw = antibody[facet.field];

  if (facet.kind !== 'named') {
    const cleaned = cleanText(raw);

    return isMeaningful(cleaned) ? [cleaned] : [];
  }

  return namesOf(raw);
};

// A URL can name a facet value that only exists as an alias ("cst" folds into
// cell-signaling-technology) or in different casing (/species/Human). Resolving it the same
// way the grouping does means those URLs serve the listing rather than a not-found page —
// they are the spellings the alias map exists to absorb.
export const resolveFacetSlug = (facet, value) => {
  const slug = slugifyFacetValue(value);

  return facet.aliases?.[slug] || slug;
};

export const getManufacturerDisplayName = (value) => {
  const cleaned = cleanText(value);
  if (!isMeaningful(cleaned)) return '';

  const slug = slugifyFacetValue(cleaned);
  const canonicalSlug = MANUFACTURER_ALIASES[slug] || slug;

  return MANUFACTURER_DISPLAY_NAMES[canonicalSlug] || cleaned;
};

export const buildFacetGroups = (antibodies, facet) => {
  const excluded = new Set(facet.excludedValues || []);
  const groups = new Map();

  antibodies.forEach((antibody) => {
    valuesOf(antibody, facet).forEach((value) => {
      const rawSlug = slugifyFacetValue(value);
      if (!rawSlug || excluded.has(rawSlug)) return;

      const canonicalSlug = facet.aliases?.[rawSlug] || rawSlug;
      const group = groups.get(canonicalSlug);

      if (group) {
        group.antibodies.push(antibody);

        return;
      }

      groups.set(canonicalSlug, {
        slug: canonicalSlug,
        name: facet.displayNames?.[canonicalSlug] || value,
        antibodies: [antibody],
      });
    });
  });

  return [...groups.values()].sort((first, second) =>
    first.name.localeCompare(second.name, undefined, { sensitivity: 'base' }),
  );
};

export const isIndexableFacetGroup = ({ antibodies }) =>
  antibodies.length >= MIN_ANTIBODIES_PER_FACET;

export const findFacetGroup = (antibodies, facet, value) => {
  const slug = resolveFacetSlug(facet, value);

  return (
    buildFacetGroups(antibodies, facet).find((group) => group.slug === slug) ||
    null
  );
};
